import React, { useState, Suspense, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import {
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import {
  ProjectModule,
  ProjectDetailTabs,
  CreateProjectRequest,
  ProjectStatus,
} from "../types";
import NavigationHeader from "../../../components/NavigationHeader";
import { useToast } from "../../../components/Toast";
import { projectModuleApi } from "../services/apiService";
import { ProjectTabEnum, PROJECT_TERMINOLOGY } from "../enums";
import { ProjectDetailViewSkeleton } from "../components/skeletons";
import ModuleActionModal from "../components/ModuleActionModal";
import ConfirmationDialog from "../components/ConfirmationDialog";
import { useProjectContext } from "../context/ProjectContext";
import ProjectBasicDetailView from "../components/ProjectBasicDetailView";
import { OperationType, UserScope } from "../../../types/operationType";
import { usePlanStatus } from "../../../hooks/usePlanStatus";

// Lazy load tab components
const ContractTab = React.lazy(() => import("../components/tabs/ContractTab"));
const SummaryTab = React.lazy(() => import("../components/tabs/SummaryTab"));
const AllocationTab = React.lazy(
  () => import("../components/tabs/AllocationTab")
);
const DocumentsTab = React.lazy(
  () => import("../components/tabs/DocumentsTab")
);
const IncomeTab = React.lazy(() => import("../components/tabs/IncomeTab"));
const IncomesTab = React.lazy(() => import("../components/tabs/IncomesTab"));
const ExpenseTab = React.lazy(() => import("../components/tabs/ExpenseTab"));
const ContactTab = React.lazy(() => import("../components/tabs/ContactTab"));
const LogsTab = React.lazy(() => import("../components/tabs/LogsTab"));
const LogsByVehicleTab = React.lazy(
  () => import("../components/tabs/LogsByVehicleTab")
);
const UsersTab = React.lazy(() => import("../components/tabs/UserTab"));
interface TabPanelProps {
  children?: React.ReactNode;
  index: ProjectDetailTabs;
  value: ProjectDetailTabs | null;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ padding: "16px 0" }}>
      {value === index && children}
    </div>
  );
};

const ProjectDetailPage: React.FC = () => {
  const { id, superProjectId } = useParams<{
    id: string;
    superProjectId?: string;
  }>();
  const navigate = useNavigate();
  const { planStatus } = usePlanStatus();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setProjectDetails } = useProjectContext();
  const [module, setModule] = useState<ProjectModule | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);

  // Get user's accessible tabs
  const getUserTabs = (): string[] => {
    // If user has GLOBAL scope, return all available tabs
    if (planStatus.user?.userRole?.scope === UserScope.GLOBAL) {
      const allTabs = Object.values(ProjectDetailTabs);
      // Hide CONTRACT and DOCUMENTS tabs in division view
      if (superProjectId) {
        return allTabs?.filter(
          (tab) =>
            tab !== ProjectDetailTabs.CONTRACT &&
            tab !== ProjectDetailTabs.DOCUMENTS
        );
      }
      return allTabs;
    }

    if (!module?.userTabs || module.userTabs.length === 0) {
      return [];
    }

    // Hide CONTRACT and DOCUMENTS tabs in division view for all users
    if (superProjectId) {
      return module.userTabs?.filter(
        (tab) =>
          tab !== ProjectDetailTabs.CONTRACT &&
          tab !== ProjectDetailTabs.DOCUMENTS
      );
    }

    return module.userTabs;
  };

  // Get initial tab based on user access and URL params
  const getInitialTab = (): ProjectDetailTabs | null => {
    const tabFromUrl = searchParams.get("tab");
    const userTabs = getUserTabs();

    // If user has no accessible tabs, return null
    if (userTabs.length === 0) {
      return null;
    }

    // If there's a valid tab in URL and user has access to it
    if (
      tabFromUrl &&
      Object.values(ProjectDetailTabs).includes(
        tabFromUrl as ProjectDetailTabs
      ) &&
      userTabs.includes(tabFromUrl)
    ) {
      return tabFromUrl as ProjectDetailTabs;
    }

    // Default to first accessible tab
    return userTabs[0] as ProjectDetailTabs;
  };

  const [activeTab, setActiveTab] = useState<ProjectDetailTabs | null>(null);

  const userTabs = getUserTabs();

  const fetchModule = async () => {
    if (!id) {
      showToast(`${PROJECT_TERMINOLOGY.SINGULAR} ID not found`, "error");
      navigate(`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`);
      return;
    }

    setIsLoading(true);
    try {
      const data = await projectModuleApi.getModule(id);
      setModule(data);
      setProjectDetails(data);
    } catch (error) {
      showToast(
        `Failed to fetch ${PROJECT_TERMINOLOGY.LOWER_SINGULAR} details`,
        "error"
      );
      navigate(`/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchModule();
  }, []);

  // Update activeTab when module loads or URL parameters change
  useEffect(() => {
    if (!module) return;

    const initialTab = getInitialTab();
    if (initialTab) {
      setActiveTab(initialTab);
      setSearchParams({ tab: initialTab });
    }
  }, [module, planStatus.user?.userRole?.scope]);

  const handleUpdateModule = async (data: CreateProjectRequest) => {
    setIsSubmitting(true);
    try {
      await projectModuleApi.updateModule({ ...data, id: module?.id });
      showToast(
        `${PROJECT_TERMINOLOGY.SINGULAR} updated successfully`,
        "success"
      );
      fetchModule();
      setActionModalOpen(false);
    } catch (error) {
      showToast(
        `Failed to save ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}`,
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTerminateModule = async (module: ProjectModule) => {
    if (!module) return;
    setIsSubmitting(true);
    try {
      await projectModuleApi.updateModuleStatus(
        module.id,
        ProjectStatus.TERMINATED
      );
      showToast(
        `${PROJECT_TERMINOLOGY.SINGULAR} terminated successfully`,
        "success"
      );
    } catch (error) {
      showToast(
        `Failed to terminate ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}`,
        "error"
      );
    } finally {
      setIsSubmitting(false);
      setTerminateDialogOpen(false);
    }
  };

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: ProjectDetailTabs
  ) => {
    setActiveTab(newValue);
    // Update URL parameters
    setSearchParams({ tab: newValue });
  };

  const handleEdit = () => {
    setActionModalOpen(true);
  };

  if (isLoading) {
    return <ProjectDetailViewSkeleton />;
  }

  if (!module) {
    return (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <NavigationHeader
          title={
            superProjectId
              ? `${ProjectTabEnum.DIVISIONS.label} Not Found`
              : `${PROJECT_TERMINOLOGY.SINGULAR} Not Found`
          }
          backNavigationTo={
            superProjectId
              ? `/super/${superProjectId}?tab=${ProjectDetailTabs.DIVISIONS}`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`
          }
          breadcrumbs={[
            {
              label: PROJECT_TERMINOLOGY.PLURAL,
              path: superProjectId
                ? `/super`
                : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
            },
            ...(superProjectId
              ? [
                  {
                    label: ProjectTabEnum.DIVISIONS.label,
                    path: `/super/${superProjectId}?tab=${ProjectDetailTabs.DIVISIONS}`,
                  },
                ]
              : []),
            {
              label: superProjectId
                ? `${ProjectTabEnum.DIVISIONS.label} Details`
                : `${PROJECT_TERMINOLOGY.SINGULAR} Details`,
            },
          ]}
        />
        <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
          {PROJECT_TERMINOLOGY.SINGULAR} not found
        </Typography>
      </Container>
    );
  }

  // If user has no accessible tabs, show access denied
  if (userTabs.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <NavigationHeader
          title={
            superProjectId
              ? `${ProjectTabEnum.DIVISIONS.label} Details`
              : `${PROJECT_TERMINOLOGY.SINGULAR} Details`
          }
          backNavigationTo={
            superProjectId
              ? `/super/${superProjectId}?tab=${ProjectDetailTabs.DIVISIONS}`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`
          }
          breadcrumbs={[
            {
              label: PROJECT_TERMINOLOGY.PLURAL,
              path: `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
            },
            ...(superProjectId
              ? [
                  {
                    label: ProjectTabEnum.DIVISIONS.label,
                    path: `/super/${superProjectId}?tab=${ProjectDetailTabs.DIVISIONS}`,
                  },
                ]
              : []),
            {
              label: superProjectId
                ? `${ProjectTabEnum.DIVISIONS.label} Details`
                : `${PROJECT_TERMINOLOGY.SINGULAR} Details`,
            },
          ]}
        />
        <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
          You don't have access to this {PROJECT_TERMINOLOGY.LOWER_SINGULAR}
        </Typography>
      </Container>
    );
  }

  // If activeTab is not set yet, show loading
  if (!activeTab) {
    return <ProjectDetailViewSkeleton />;
  }

  const allTabConfig = [
    {
      key: ProjectDetailTabs.SUMMARY,
      label: ProjectTabEnum.SUMMARY.label,
      icon: React.cloneElement(ProjectTabEnum.SUMMARY.icon, {
        sx: { fontSize: "1rem" },
      }),
    },
    {
      key: ProjectDetailTabs.LOGS,
      label: ProjectTabEnum.LOGS.label,
      icon: React.cloneElement(ProjectTabEnum.LOGS.icon, {
        sx: { fontSize: "1rem" },
      }),
    },
    {
      key: ProjectDetailTabs.EXPENSE,
      label: ProjectTabEnum.EXPENSES.label,
      icon: React.cloneElement(ProjectTabEnum.EXPENSES.icon, {
        sx: { fontSize: "1rem" },
      }),
    },
    {
      key: ProjectDetailTabs.INCOME,
      label: ProjectTabEnum.INCOME.label,
      icon: React.cloneElement(ProjectTabEnum.INCOME.icon, {
        sx: { fontSize: "1rem" },
      }),
    },
    {
      key: ProjectDetailTabs.ALLOCATIONS,
      label: ProjectTabEnum.ALLOCATIONS.label,
      icon: React.cloneElement(ProjectTabEnum.ALLOCATIONS.icon, {
        sx: { fontSize: "1rem" },
      }),
    },
    // Only show DOCUMENTS tab when not in division view
    ...(!superProjectId
      ? [
          {
            key: ProjectDetailTabs.DOCUMENTS,
            label: ProjectTabEnum.DOCUMENTS.label,
            icon: React.cloneElement(ProjectTabEnum.DOCUMENTS.icon, {
              sx: { fontSize: "1rem" },
            }),
          },
        ]
      : []),
    {
      key: ProjectDetailTabs.USERS,
      label: ProjectTabEnum.USERS.label,
      icon: React.cloneElement(ProjectTabEnum.USERS.icon, {
        sx: { fontSize: "1rem" },
      }),
    },
    // Only show CONTRACT tab when not in division view
    ...(!superProjectId
      ? [
          {
            key: ProjectDetailTabs.CONTRACT,
            label: ProjectTabEnum.CONTRACT.label,
            icon: React.cloneElement(ProjectTabEnum.CONTRACT.icon, {
              sx: { fontSize: "1rem" },
            }),
          },
        ]
      : []),
  ];

  // Filter tabs based on user's access
  const tabConfig = allTabConfig.filter((tab) => userTabs.includes(tab.key));

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        title={
          superProjectId
            ? `${ProjectTabEnum.DIVISIONS.label} Details`
            : `${PROJECT_TERMINOLOGY.SINGULAR} Details`
        }
        backNavigationTo={
          superProjectId
            ? `/super/${superProjectId}?tab=${ProjectDetailTabs.DIVISIONS}`
            : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`
        }
        breadcrumbs={[
          {
            label: PROJECT_TERMINOLOGY.PLURAL,
            path: superProjectId
              ? `/super`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
          },
          ...(superProjectId
            ? [
                {
                  label: ProjectTabEnum.DIVISIONS.label,
                  path: `/super/${superProjectId}?tab=${ProjectDetailTabs.DIVISIONS}`,
                },
              ]
            : []),
          {
            label: superProjectId
              ? `${ProjectTabEnum.DIVISIONS.label} Details`
              : `${PROJECT_TERMINOLOGY.SINGULAR} Details`,
          },
        ]}
        buttons={
          planStatus.user?.userRole?.scope === UserScope.GLOBAL
            ? [
                {
                  label: `Edit ${PROJECT_TERMINOLOGY.SINGULAR}`,
                  onClick: handleEdit,
                  icon: <EditIcon />,
                  variant: "outlined",
                },
                // ...(module.status !== ProjectStatus.TERMINATED
                //   ? [
                //       {
                //         label: "Terminate",
                //         onClick: () => setTerminateDialogOpen(true),
                //         icon: <CancelIcon sx={{ fontSize: "0.6rem" }} />,
                //         variant: "outlined" as const,
                //         color: "error" as const,
                //       },
                //     ]
                //   : []),
              ]
            : []
        }
      />

      {/* Basic Project Information */}
      <ProjectBasicDetailView projectDetails={module} />

      {/* Tabs Section */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTab || false}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTab-root": {
              fontSize: "0.75rem",
              minHeight: "48px",
              textTransform: "none",
            },
          }}
        >
          {tabConfig.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              onClick={() => {
                setActiveTab(tab.key);
                setSearchParams({ tab: tab.key });
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        <Suspense
        // fallback={
        //   <Loading message="Loading tab content..." fullScreen={false} />
        // }
        >
          {userTabs.includes(ProjectDetailTabs.SUMMARY) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.SUMMARY}>
              <SummaryTab
                projectId={module.id}
                setActiveTab={setActiveTab}
                superProjectId={superProjectId}
              />
            </TabPanel>
          )}
          {userTabs.includes(ProjectDetailTabs.ALLOCATIONS) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.ALLOCATIONS}>
              <AllocationTab
                projectId={module.id}
                superProjectId={superProjectId}
              />
            </TabPanel>
          )}
          {userTabs.includes(ProjectDetailTabs.DOCUMENTS) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.DOCUMENTS}>
              <DocumentsTab
                projectId={module.id}
                superProjectId={superProjectId}
              />
            </TabPanel>
          )}
          {userTabs.includes(ProjectDetailTabs.LOGS) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.LOGS}>
              {planStatus?.operationType === OperationType.LARGE_FLEET ||
              planStatus?.operationType === OperationType.LARGE_FLEET_ALL ? (
                <LogsByVehicleTab
                  projectId={module.id}
                  superProjectId={superProjectId}
                />
              ) : (
                <LogsTab
                  projectId={module.id}
                  superProjectId={superProjectId}
                />
              )}
            </TabPanel>
          )}
          {userTabs.includes(ProjectDetailTabs.EXPENSE) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.EXPENSE}>
              <ExpenseTab
                projectId={module.id}
                superProjectId={superProjectId}
              />
            </TabPanel>
          )}
          {userTabs.includes(ProjectDetailTabs.INCOME) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.INCOME}>
              <IncomesTab
                projectId={module.id}
                superProjectId={superProjectId}
              />
            </TabPanel>
          )}

          {userTabs.includes(ProjectDetailTabs.USERS) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.USERS}>
              <UsersTab projectId={module.id} superProjectId={superProjectId} />
            </TabPanel>
          )}
          {userTabs.includes(ProjectDetailTabs.CONTRACT) && (
            <TabPanel value={activeTab} index={ProjectDetailTabs.CONTRACT}>
              <ContractTab
                projectId={module.id}
                isDivision={Boolean(superProjectId)}
              />
            </TabPanel>
          )}
        </Suspense>
      </Box>
      {/* Modals */}
      <ModuleActionModal
        open={actionModalOpen}
        onClose={() => setActionModalOpen(false)}
        module={module}
        onSubmit={handleUpdateModule}
        isSubmitting={isSubmitting}
      />

      {/* Terminate Module Dialog */}
      <ConfirmationDialog
        deleteModalOpen={terminateDialogOpen}
        handleNo={() => setTerminateDialogOpen(false)}
        handleYes={handleTerminateModule}
        itemToDelete={module}
        title={`Terminate ${PROJECT_TERMINOLOGY.SINGULAR}`}
        message={`Are you sure you want to terminate this ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}? This action cannot be undone.`}
        action="Terminate"
      />
    </Container>
  );
};

export default ProjectDetailPage;
